import _ from 'lodash'
import moment from 'moment-timezone'
import {
    FC,
    PropsWithChildren,
    Dispatch as ReactDispatch,
    SetStateAction,
} from 'react'
import { DrawerCheckBoxSourceType } from '@ProdWb/types'
import { Row, Col, Drawer, Collapse, DatePicker } from 'antd'
import { Dispatch } from 'redux'
import { SorterResult } from 'antd/lib/table/interface'
import { PaginationConfig } from 'antd/lib/pagination'
import { RangeValue } from 'rc-picker/lib/interface'
import { useLocation } from 'react-use'
import DrawerRange from '@ProdWb/components/FilterDrawer/DrawerRange'
import { useShowAdditionalQualityFilterFlag } from '@OpsWb/logic'
import styles from '@OpsQuality/styles.css'

import Box from '~/core/Box'
import Button from '~/core/Button'
import getColor from '~/lib/getColor'
import { getCollapseIcon } from '~/lib/getCollapseIcon'
import {
    DATE_FORMAT,
    INSPECTION_STATUS_FILTER,
    JobRequestListPagePlacement,
} from '../constants'
import { UrlFilterType } from '../types'

import JobRequestListMobileTable from './JobRequestListMobileTable'
import JobRequestListTable from './JobRequestListTable'
import {
    getDateRange,
    getUrlFilter,
    RANGE_PICKER_RANGE_CRITERIA,
    useMPCountries,
} from './logic'
import { DrawerCheckbox } from './TableFilters'
import {
    getAdminJobRequestsQuality_adminJobRequests,
    getAdminJobRequestsQuality_adminJobRequests_items as JobRequestType,
} from './__generated__/getAdminJobRequestsQuality'

const { RangePicker } = DatePicker

type FiltersPanelType = {
    data?: {
        adminJobRequests: getAdminJobRequestsQuality_adminJobRequests
    }
    loading: boolean
    handleTableSorterChange: (
        sorter: SorterResult<JobRequestType> | SorterResult<JobRequestType>[],
    ) => void
    paginationInfo: PaginationConfig | undefined
    onChange: (page: SetStateAction<number>) => void
    pageNumber: number
    sort?: string
    onFilterChange: (
        pagination: PaginationConfig,
        sorter: SorterResult<JobRequestType>,
    ) => void
    setFilterDrawerVisible: ReactDispatch<SetStateAction<boolean>>
    filterDrawerVisible: boolean
    pendingFromDate?: moment.Moment | null
    pendingToDate?: moment.Moment | null
    onDateChange: (
        _dates: RangeValue<moment.Moment>,
        [selectedFromDateString, selectedToDateString]: [string, string],
    ) => void
    pendingStatusFilters?: string[] | null
    setPendingStatusFilters: ReactDispatch<
        SetStateAction<string[] | null | undefined>
    >
    pendingPriorityFilters?: string[] | null
    setPendingPriorityFilters: ReactDispatch<
        SetStateAction<string[] | null | undefined>
    >
    pendingProcessTypeFilters?: string[] | null
    setPendingProcessTypeFilters: ReactDispatch<
        SetStateAction<string[] | null | undefined>
    >
    pendingBusinessUnitFilters?: string[] | null
    setPendingBusinessUnitFilters: ReactDispatch<
        SetStateAction<string[] | null | undefined>
    >
    inspectionStatusFilter?: string[] | null
    setInspectionStatusFilter: ReactDispatch<
        SetStateAction<string[] | null | undefined>
    >
    payoutFilter?: string[] | null
    setPayoutFilter: ReactDispatch<SetStateAction<string[] | null | undefined>>
    mfgDriFilter?: string[] | null
    setMfgDriFilter: ReactDispatch<SetStateAction<string[] | null | undefined>>
    countryFilters?: string[] | null
    setCountryFilters: ReactDispatch<
        SetStateAction<string[] | null | undefined>
    >
    applyFilters: () => void
    clearFilters: () => ReturnType<Dispatch>
    processTypes?: DrawerCheckBoxSourceType[]
    businessUnits?: DrawerCheckBoxSourceType[]
    salesDriData?: DrawerCheckBoxSourceType[]
    mpCountries: ReturnType<typeof useMPCountries>
}
const FiltersPanel: FC<PropsWithChildren<FiltersPanelType>> = props => {
    const {
        data,
        loading,
        handleTableSorterChange,
        paginationInfo,
        onChange,
        pageNumber,
        sort,
        onFilterChange,
        setFilterDrawerVisible,
        filterDrawerVisible,
        pendingFromDate,
        pendingToDate,
        onDateChange,
        pendingStatusFilters,
        setPendingStatusFilters,
        pendingPriorityFilters,
        setPendingPriorityFilters,
        pendingProcessTypeFilters,
        setPendingProcessTypeFilters,
        pendingBusinessUnitFilters,
        setPendingBusinessUnitFilters,
        inspectionStatusFilter,
        setInspectionStatusFilter,
        payoutFilter,
        setPayoutFilter,
        mfgDriFilter,
        setMfgDriFilter,
        countryFilters,
        setCountryFilters,
        applyFilters,
        clearFilters,
        processTypes,
        businessUnits,
        salesDriData,
        mpCountries,
    } = props
    const location = useLocation()
    const items = _.get(data, 'adminJobRequests.items', [])
    const formattedItems = items.map((item: JobRequestType) => ({
        key: item.id,
        ...item,
    }))
    const showAdditionalFilters = useShowAdditionalQualityFilterFlag()
    const urlFilters: UrlFilterType = getUrlFilter(location)
    const {
        fromDate,
        toDate,
        statuses,
        priorityTypes,
        platformProcessTypeIds,
        businessUnits: businessUnitsUrlFilters,
        inspectionStatus,
        mfgDriIds,
        payout,
        countryIds,
    } = urlFilters

    return (
        <Box direction="col" className={styles.jobRequestListBox}>
            <Row>
                <Col xs={0} md={24}>
                    <JobRequestListTable
                        loading={loading}
                        items={formattedItems}
                        onSorterChange={handleTableSorterChange}
                        paginationInfo={paginationInfo}
                        onPageChange={onChange}
                        pageNumber={pageNumber}
                        totalCount={data?.adminJobRequests?.totalCount || 0}
                    />
                </Col>
                <Col xs={24} md={0}>
                    <JobRequestListMobileTable
                        loading={loading}
                        items={formattedItems}
                        sortedInfo={sort}
                        paginationInfo={paginationInfo}
                        onFilterChange={onFilterChange}
                    />
                </Col>
            </Row>
            <Drawer
                title="Filters"
                placement="right"
                onClose={evt => {
                    evt.preventDefault()
                    setFilterDrawerVisible(!filterDrawerVisible)
                }}
                visible={filterDrawerVisible}
                width={380}
                bodyStyle={{ padding: 0 }}
            >
                <Collapse
                    bordered={false}
                    expandIconPosition="right"
                    expandIcon={getCollapseIcon}
                    style={{ paddingBottom: '90px' }}
                    defaultActiveKey="date"
                >
                    <Collapse.Panel
                        header="Business unit"
                        key="businessUnit"
                        extra={
                            (pendingBusinessUnitFilters &&
                                pendingBusinessUnitFilters?.length > 0) ||
                            businessUnitsUrlFilters?.length > 0 ? null : (
                                <div style={{ fontSize: '11px' }}>Any</div>
                            )
                        }
                    >
                        <DrawerCheckbox
                            pendingFilters={pendingBusinessUnitFilters}
                            selectedFilters={businessUnitsUrlFilters}
                            setCheckboxFilter={setPendingBusinessUnitFilters}
                            options={businessUnits}
                            filterName="businessUnit"
                        />
                    </Collapse.Panel>
                    <Collapse.Panel
                        header="POR dock date (MP due date) is"
                        key="date"
                        extra={
                            pendingFromDate ||
                            pendingToDate ||
                            pendingToDate ? null : (
                                <div style={{ fontSize: '11px' }}>Any</div>
                            )
                        }
                    >
                        <RangePicker
                            className={styles.dateInput}
                            format={DATE_FORMAT}
                            onChange={onDateChange}
                            dropdownClassName={
                                styles.rangePickerResponsiveLayout
                            }
                            value={getDateRange(
                                fromDate,
                                toDate,
                                pendingFromDate,
                                pendingToDate,
                            )}
                            ranges={RANGE_PICKER_RANGE_CRITERIA}
                        />
                    </Collapse.Panel>
                    <Collapse.Panel
                        header="Job request status"
                        key="status"
                        extra={
                            (pendingStatusFilters &&
                                pendingStatusFilters?.length > 0) ||
                            statuses?.length > 0 ? null : (
                                <div style={{ fontSize: '11px' }}>Any</div>
                            )
                        }
                    >
                        <DrawerCheckbox
                            pendingFilters={pendingStatusFilters}
                            selectedFilters={statuses}
                            setCheckboxFilter={setPendingStatusFilters}
                            filterName="status"
                        />
                    </Collapse.Panel>
                    <Collapse.Panel
                        header="Inspection status"
                        key="inspectionStatus"
                    >
                        <DrawerCheckbox
                            pendingFilters={inspectionStatusFilter}
                            selectedFilters={inspectionStatus}
                            setCheckboxFilter={setInspectionStatusFilter}
                            filterName="inspectionStatus"
                            options={INSPECTION_STATUS_FILTER}
                        />
                    </Collapse.Panel>
                    <Collapse.Panel
                        header="Priority"
                        key="priority"
                        extra={
                            (pendingPriorityFilters &&
                                pendingPriorityFilters?.length > 0) ||
                            priorityTypes?.length > 0 ? null : (
                                <div style={{ fontSize: '11px' }}>Any</div>
                            )
                        }
                    >
                        <DrawerCheckbox
                            pendingFilters={pendingPriorityFilters}
                            selectedFilters={priorityTypes}
                            setCheckboxFilter={setPendingPriorityFilters}
                            filterName="priority"
                        />
                    </Collapse.Panel>
                    <Collapse.Panel
                        header="Process type"
                        key="processtype"
                        extra={
                            (pendingProcessTypeFilters &&
                                pendingProcessTypeFilters?.length > 0) ||
                            platformProcessTypeIds?.length > 0 ? null : (
                                <div style={{ fontSize: '11px' }}>Any</div>
                            )
                        }
                    >
                        <DrawerCheckbox
                            pendingFilters={pendingProcessTypeFilters}
                            selectedFilters={platformProcessTypeIds}
                            setCheckboxFilter={setPendingProcessTypeFilters}
                            options={processTypes}
                        />
                    </Collapse.Panel>
                    {showAdditionalFilters && (
                        <Collapse.Panel header="Payout" key="payout">
                            <DrawerRange
                                handleRangeFilter={(
                                    filterType,
                                    filterValues,
                                ) => {
                                    const stringValues = filterValues.map(
                                        value => value.toString(),
                                    )

                                    setPayoutFilter(
                                        filterValues.length > 0
                                            ? stringValues
                                            : null,
                                    )
                                }}
                                filterType="payout"
                                selectedValues={
                                    payoutFilter === undefined &&
                                    payout?.length === 2
                                        ? payout
                                        : []
                                }
                            />
                        </Collapse.Panel>
                    )}
                    {showAdditionalFilters && (
                        <Collapse.Panel
                            header="MFG DRI"
                            key="mfgdri"
                            extra={
                                (mfgDriFilter && mfgDriFilter?.length > 0) ||
                                mfgDriIds?.length > 0 ? null : (
                                    <div
                                        style={{
                                            fontSize: '11px',
                                        }}
                                    >
                                        Any
                                    </div>
                                )
                            }
                        >
                            <DrawerCheckbox
                                pendingFilters={mfgDriFilter}
                                selectedFilters={mfgDriIds}
                                setCheckboxFilter={setMfgDriFilter}
                                options={salesDriData}
                            />
                        </Collapse.Panel>
                    )}
                    <Collapse.Panel
                        header="MP country"
                        key="country"
                        extra={
                            (countryFilters && countryFilters?.length > 0) ||
                            countryIds?.length > 0 ? null : (
                                <div style={{ fontSize: '11px' }}>All</div>
                            )
                        }
                    >
                        <DrawerCheckbox
                            pendingFilters={countryFilters}
                            selectedFilters={countryIds}
                            setCheckboxFilter={setCountryFilters}
                            options={mpCountries}
                        />
                    </Collapse.Panel>
                </Collapse>
                <Box
                    direction="col"
                    textAlign="center"
                    padding="10px"
                    background={getColor('white')}
                    className={styles.drawerFooterStyle}
                >
                    <Button
                        name="Apply Filters"
                        placement={JobRequestListPagePlacement}
                        type="primary"
                        style={{ marginBottom: 5 }}
                        onClick={applyFilters}
                    >
                        Apply
                    </Button>
                    <Button
                        name="Clear Filters"
                        placement={JobRequestListPagePlacement}
                        onClick={clearFilters}
                    >
                        Clear
                    </Button>
                </Box>
            </Drawer>
        </Box>
    )
}

export default FiltersPanel
